/**
 * @architecture-header 1
 * @layer host
 * @domain browser-storage
 * @responsibility Clear browser authentication and transient state while preserving language preference.
 * @state-owner browser local and session storage
 * @allowed-imports browser Storage interface
 * @forbidden domain state; Supabase client; React
 * @public-api local-run and sign-out cleanup helpers
 * @side-effects mutates browser storage
 * @tests browser-storage.test.ts
 * @security-boundary browser persistence
 * @data-classification authentication state
 */

const LOCAL_RUN_MARKER = 'vispo:local-run-id';
const LANGUAGE_KEY_PATTERN = /(locale|language|i18n)/i;

function preservedLanguageValues(storage: Storage): Array<[string, string]> {
  const values: Array<[string, string]> = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key || !LANGUAGE_KEY_PATTERN.test(key)) continue;
    const value = storage.getItem(key);
    if (value !== null) values.push([key, value]);
  }
  return values;
}

function clearAndRestoreLanguage(storage: Storage): void {
  const preserved = preservedLanguageValues(storage);
  storage.clear();
  for (const [key, value] of preserved) storage.setItem(key, value);
}

export function clearBrowserTransientState(
  local: Storage = globalThis.localStorage,
  session: Storage = globalThis.sessionStorage,
): void {
  clearAndRestoreLanguage(local);
  session.clear();
}

export function resetBrowserStateForLocalRun(
  runId: string,
  local: Storage = globalThis.localStorage,
  session: Storage = globalThis.sessionStorage,
): void {
  if (!runId || local.getItem(LOCAL_RUN_MARKER) === runId) return;
  clearBrowserTransientState(local, session);
  local.setItem(LOCAL_RUN_MARKER, runId);
}
